import React from 'react'
import { useImmer } from 'use-immer'
import useInterval from 'hooks/Interval.js'

import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import CircularProgress from '@material-ui/core/CircularProgress'
import LinearProgress from '@material-ui/core/LinearProgress'
import Snackbar from '@material-ui/core/Snackbar'
import Alert from '@material-ui/lab/Alert'

import Card from '../../components/Card/Card.js'
import CardBody from '../../components/Card/CardBody.js'
import CardIcon from '../../components/Card/CardIcon.js'
import CardHeader from '../../components/Card/CardHeader.js'

import SiteSelector from '../Common/SiteSelector.js'
import AtlasButtonBar from 'components/AtlasButtonBar/AtlasButtonBar.js'
import UnsavedChangesPrompt from '../../components/UnsavedChangesPrompt/UnsavedChangesPrompt.js'

import HotkeyMenu from './HotkeyMenu.js'
import HotkeyNavigationBar from './HotkeyNavigationBar.js'
import HotkeyElementEditor from './HotkeyElementEditor.js'
import UnsavedMenuDialog, { UnsavedMenuDialogModes } from './UnsavedMenuDialog.js'
import DeleteElementDialog from './DeleteElementDialog.js'
import SelectProductDialog from '../Common/SelectProductDialog.js'
import GlassPanel from './GlassPanel.js'
import PublishDialog from './PublishDialog.js'
import PublishingPanel from './PublishingPanel.js'

import AppsIcon from '@material-ui/icons/Apps'
import SaveIcon from '@material-ui/icons/Save'
import HeightIcon from '@material-ui/icons/Height'
// import WrapTextIcon from '@material-ui/icons/WrapText'
import LaunchIcon from '@material-ui/icons/Launch'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import TitleIcon from '@material-ui/icons/Title'

import { ReactComponent as AllCategories } from 'assets/img/icons/hotkeys/light/AllCategories.svg'
import { ReactComponent as NoChosenCategory } from 'assets/img/icons/hotkeys/light/NoChosenCategory.svg'

import { dashboardResponse, getHotkeyElements, saveHotkeyElements, getHotkeyLookups, publishHotkeys } from 'layouts/Aws'

import { v4 as uuidv4 } from 'uuid'

import { makeStyles } from '@material-ui/core/styles'
import { getHotkeysPublishStatus } from 'layouts/Aws.js'

const useStyles = makeStyles((theme) => ({
  leftPanel: {
    [theme.breakpoints.up('lg')]: { // we only add the padding when we are displaying both panels on the same grid line
      paddingRight: 5
    },
    marginBottom: 10
  },
  rightPanel: {
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 5
    }
  },
  panel: {
    position: 'relative',
    border: 'solid 1px #b9bfc7',
    backgroundColor: '#f8f8f8'
  },
  menuWrapper: {
    height: '540px',
    overflowY: 'auto',
    paddingBottom: '30px' // allow the fade to have room
  },
  menuFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '50px'
  }
}))

export default function HotkeyEditor () {
  const [isLookupDataLoading, setLookupDataLoading] = React.useState(false)
  const [isMenuLoading, setMenuLoading] = React.useState(false)
  const [siteList, setSiteList] = React.useState()
  const [selectedSiteId, setSelectedSiteId] = React.useState(null)

  const [menuData, setMenuData] = useImmer([])

  const [currentMenu, setCurrentMenu] = React.useState(null) // ref to the currently selected menu
  const [currentSubMenu, setCurrentSubMenu] = React.useState(null) // ref to the currently selected sub menu
  const [currentMenuId, setCurrentMenuId] = React.useState(null) // this just refers to the id of the current menu we are in - could be normal or submenu
  const [currentMenuType, setCurrentMenuType] = React.useState('root') // root, menu or submenu - so we can decide which buttons to enable / disable
  const [selectedElementIndex, setSelectedElementIndex] = React.useState(null)

  const [isSaving, setSaving] = React.useState(false)
  const [saveButtonText, setSaveButtonText] = React.useState('Save')
  const [menuUpdated, setMenuUpdated] = React.useState(false)
  const [unsavedMenuDialogOpen, setUnsavedMenuDialogOpen] = React.useState(false)
  const [unsavedMenuDialogElement, setUnsavedMenuDialogElement] = React.useState(null)
  const [unsavedMenuDialogMode, setUnsavedMenuDialogMode] = React.useState(UnsavedMenuDialogModes.CHANGE_MENU)
  const [deletedElements, setDeletedElements] = useImmer([])
  const [deleteElementDialogOpen, setDeleteElementDialogOpen] = React.useState(false)
  const [publishDialogOpen, setPublishDialogOpen] = React.useState(false)
  const [isPublishing, setPublishing] = React.useState(false)

  const [fontSizes, setFontSizes] = React.useState({})
  const [widths, setWidths] = React.useState({})
  const [icons, setIcons] = React.useState([])
  const [colors, setColors] = React.useState([])
  const [colorsById, setColorsById] = React.useState({})
  const [iconsById, setIconsById] = React.useState({})
  const [fontSizesByName, setFontSizesByName] = React.useState({})
  const [widthsByName, setWidthsByName] = React.useState({})

  const [productSelectDialogOpen, setProductSelectDialogOpen] = React.useState(false)

  const [hotkeyElementEditorFormValid, setHotkeyElementEditorFormValid] = React.useState(true)

  const [snackbarOpen, setSnackbarOpen] = React.useState(false)
  const [snackbarMessage, setSnackbarMessage] = React.useState('')
  const [snackbarSeverity, setSnackbarSeverity] = React.useState('error')

  const classes = useStyles()

  React.useEffect(() => {
    dashboardResponse().then(res => {
      setSiteList(res.sites)
    })
  }, [])

  React.useEffect(() => {
    if (selectedSiteId !== null) {
      loadHotkeyMenu(selectedSiteId, null)
    }
  }, [selectedSiteId])

  React.useEffect(() => {
    if (hotkeyElementEditorFormValid) {
      setSnackbarOpen(false)
    } else {
      setSnackbarMessage('Please fix validation errors to continue')
      setSnackbarSeverity('warning')
      setSnackbarOpen(true)
    }
  }, [hotkeyElementEditorFormValid])

  const onSiteSelected = async (siteId) => {
    setPublishing(false)
    setLookupDataLoading(true)

    await getLookupData(siteId)
    await getPublishingStatus(siteId) // set the publishing state upon initial load

    setLookupDataLoading(false)

    setSelectedSiteId(siteId)
  }

  const getPublishingStatus = async (siteId) => {
    const publishStatus = await getHotkeysPublishStatus(siteId)

    if (publishStatus.state !== null) {
      setPublishing(true)
    } else {
      setPublishing(false)
    }
  }

  // when we set the isPublishing state to true, poll the publishing status every 5 seconds, and if publishing has finished, stop the polling
  useInterval(() => {
    getPublishingStatus(selectedSiteId)
  }, isPublishing ? 5000 : null)

  const getLookupData = async (siteId) => {
    const lookupData = await getHotkeyLookups(siteId)

    setWidths(lookupData.widths)
    setFontSizes(lookupData.fontSizes)
    setIcons(lookupData.icons)
    setColors(lookupData.colors)

    // as well as arrays of the lookups we use for passing into the HotkeyElementEditor, we create lookups keyed on the relevant id / name of each set of lookup data
    const _colorsById = {}

    for (const color of lookupData.colors) {
      _colorsById[color.colorId] = color
    }

    setColorsById(_colorsById)

    const _iconsById = {}

    for (const icon of lookupData.icons) {
      _iconsById[icon.iconId] = icon
    }

    setIconsById(_iconsById)

    const _fontSizesByName = {}

    for (const fontSize of lookupData.fontSizes) {
      _fontSizesByName[fontSize.name] = fontSize
    }

    setFontSizesByName(_fontSizesByName)

    const _widthsByName = {}

    for (const width of lookupData.widths) {
      _widthsByName[width.name] = width
    }

    setWidthsByName(_widthsByName)
  }

  const loadHotkeyMenu = async (siteId, parentId) => {
    setMenuLoading(true)

    const hotkeyElements = await getHotkeyElements(siteId, parentId)

    const menuData = []

    for (const element of hotkeyElements) {
      menuData.push({
        id: element.id,
        type: element.type,
        width: element.width,
        widthValue: element.width !== null ? widthsByName[element.width].value : null,
        text: element.text,
        originalText: element.originalText,
        productId: element.productId,
        barcode: element.barcode,
        icon: element.iconId !== null ? iconsById[element.iconId].name : null,
        iconId: element.iconId,
        fontSize: element.fontSize,
        fontSizeValue: element.fontSize !== null ? fontSizesByName[element.fontSize].value : null,
        itemSize: element.itemSizeDescription,
        originalItemSize: element.originalItemSizeDescription,
        price: element.price,
        color: element.colorId !== null ? colorsById[element.colorId].value : null,
        colorId: element.colorId,
        priority: element.priority
      })
    }

    setMenuData(menuData)
    setCurrentMenuId(parentId)

    setMenuLoading(false)
  }

  const handleElementClick = (event, elementId, element) => {
    if (!hotkeyElementEditorFormValid) {
      return
    }

    for (let i = 0; i < menuData.length; i++) {
      if (menuData[i].id === elementId) {
        setSelectedElementIndex(i)
        break
      }
    }
  }

  const handleElementDoubleClick = async (event, elementId, element) => {
    attemptToOpenMenu(element)
  }

  const attemptToOpenMenu = async (element) => {
    if (!hotkeyElementEditorFormValid) {
      return
    }

    setUnsavedMenuDialogMode(UnsavedMenuDialogModes.CHANGE_MENU)
    setUnsavedMenuDialogElement(element)
    setUnsavedMenuDialogOpen(true)
  }

  // this should only be called when we have no unsaved elements
  const openMenu = async (element) => {
    setSelectedElementIndex(null)

    if (element !== null) {
      if (element.type === 'menu') {
        setCurrentMenu(element)
      } else if (element.type === 'submenu') {
        setCurrentSubMenu(element)
      }

      setCurrentMenuType(element.type)
      await loadHotkeyMenu(selectedSiteId, element.id)
    } else {
      loadHotkeyMenu(selectedSiteId, null)
      setCurrentMenuType('root')
    }
  }

  const handleOpenSelectedMenu = () => {
    attemptToOpenMenu(menuData[selectedElementIndex])
  }

  const handleUnsavedMenuDialogContinue = async (menuElement, mode) => {
    setUnsavedMenuDialogOpen(false)

    if (menuUpdated) {
      await saveMenu()
    }

    console.log('menuElement:', menuElement, 'mode:', mode)

    if (mode === UnsavedMenuDialogModes.CHANGE_MENU) {
      // this should only be called when we have no unsaved elements
      openMenu(menuElement)
    } else {
      setPublishDialogOpen(true)
    }
  }

  const handleUnsavedMenuDialogClose = () => {
    setUnsavedMenuDialogOpen(false)
  }

  const handleNavigationHomeClick = async (event) => {
    if (!hotkeyElementEditorFormValid) {
      return
    }

    setCurrentMenu(null)
    setCurrentSubMenu(null)
    setSelectedElementIndex(null)
    setCurrentMenuType('root')

    attemptToOpenMenu(null)
  }

  const handleNavigationMenuClick = async (event, menuElement) => {
    if (!hotkeyElementEditorFormValid) {
      return
    }

    setCurrentSubMenu(null)
    setSelectedElementIndex(null)
    setCurrentMenuType('menu')

    attemptToOpenMenu(menuElement)
  }

  const handleElementDataChanged = (data) => {
    setMenuUpdated(true)

    console.log('handle data changed:', data)

    // update the hotkey elements to reflect the data change
    setMenuData((draft) => {
      const element = draft.find((element) => element.id === data.id)

      element.text = data.text
      element.color = data.colorId !== '' ? colorsById[data.colorId].value : ''
      element.colorId = data.colorId
      element.itemSize = data.itemSize
      element.width = data.width
      element.widthValue = data.width !== '' ? widthsByName[data.width].value : ''
      element.fontSize = data.fontSize
      element.fontSizeValue = data.fontSize !== '' ? fontSizesByName[data.fontSize].value : ''
      element.icon = data.iconId !== '' ? iconsById[data.iconId].name : ''
      element.iconId = data.iconId
      element.itemSize = data.itemSize
    })
  }

  const handleAddElementClick = (type) => {
    const newElementPriority = menuData.length

    const newElement = {
      id: uuidv4(),
      type: type,
      priority: newElementPriority, // new element will always be added onto the end of the list
      originalText: null,
      price: null,
      originalItemSize: null,
      width: 'medium',
      fontSize: 'medium',
      productId: null
    }

    if (type === 'product') {
      newElement.colorId = 1
      newElement.color = colorsById[1].value
    }

    if (type !== 'spacer') {
      newElement.text = 'New ' + type
    }

    if (type === 'menu') {
      newElement.icon = iconsById[8].name
      newElement.iconId = 8
    }

    setMenuData((draft) => {
      draft.push(newElement)
    })

    setMenuUpdated(true)
    setSelectedElementIndex(newElementPriority)
  }

  const handleDeleteElementClick = (event, selectedElementId, type) => {
    setDeleteElementDialogOpen(true)
  }

  const handleDeleteElement = () => {
    deleteSelectedElement()

    setDeleteElementDialogOpen(false)
  }

  const handleDeleteElementDialogClose = () => {
    setDeleteElementDialogOpen(false)
  }

  const deleteSelectedElement = () => {
    setDeletedElements((draft) => {
      draft.push(menuData[selectedElementIndex].id)
    })

    setMenuData((draft) => {
      if (selectedElementIndex !== null) {
        draft.splice(selectedElementIndex, 1)
      }
    })

    setSelectedElementIndex(null)
    setMenuUpdated(true)
  }

  const updateElementOrder = (selectedElementId, direction) => {
    let newIndex

    setMenuData((draft) => {
      // remove the selectedElement we want to move, and then re-insert it one position before or after its previous position
      if (selectedElementIndex !== null) {
        draft.splice(selectedElementIndex, 1)

        if (direction === 'up') {
          newIndex = selectedElementIndex - 1 < 0 ? 0 : selectedElementIndex - 1
        } else {
          newIndex = selectedElementIndex + 1 < menuData.length ? selectedElementIndex + 1 : menuData.length
        }

        draft.splice(newIndex, 0, {
          ...menuData[selectedElementIndex]
        })

        for (let i = 0; i < draft.length; i++) {
          draft[i].priority = i // for saving back to the db we need to update every elements priority based on their array position
        }
      }
    })

    setSelectedElementIndex(newIndex)
    setMenuUpdated(true)
  }

  const handleElementPriorityUpClick = (selectedElementId) => {
    updateElementOrder(selectedElementId, 'up')
  }

  const handleElementPriorityDownClick = (selectedElementId) => {
    updateElementOrder(selectedElementId, 'down')
  }

  const handleSaveClick = async () => {
    await saveMenu()
  }

  const saveMenu = async () => {
    setSaving(true)
    setSaveButtonText('Saving')

    await saveHotkeyElements(selectedSiteId, currentMenuId, menuData, deletedElements)

    setMenuUpdated(false)
    setSaveButtonText('Save')
    setSaving(false)
  }

  const handleProductSelectClick = () => {
    setProductSelectDialogOpen(true)
  }

  const handleProductSelect = (product) => {
    setProductSelectDialogOpen(false)

    setMenuData((draft) => {
      draft[selectedElementIndex].productId = product.productId
      draft[selectedElementIndex].barcode = product.barcode
      draft[selectedElementIndex].text = product.productDescription
      draft[selectedElementIndex].originalText = product.productDescription
      draft[selectedElementIndex].price = product.price
      draft[selectedElementIndex].itemSize = product.itemSizeDesc
      draft[selectedElementIndex].originalItemSize = product.itemSizeDesc
    })

    setMenuUpdated(true)
  }

  const handleProductSelectDialogClose = () => {
    setProductSelectDialogOpen(false)
  }

  const handlePublishDialogClose = () => {
    setPublishDialogOpen(false)
  }

  const handlePublish = async () => {
    setPublishing(true)

    setPublishDialogOpen(false)

    await publishHotkeys(selectedSiteId)
  }

  const handleHotkeyElementEditorFormValidationStatusChange = (status) => {
    setHotkeyElementEditorFormValid(status)
  }

  const handleSnackbarClose = (event, reason) => {
    console.log('handleSnackbarClose', event, reason)

    if (reason === 'timeout') {
      setSnackbarOpen(false)
    }
  }

  const attemptToPublishHotkeys = () => {
    if (!hotkeyElementEditorFormValid) {
      return
    }

    setUnsavedMenuDialogMode(UnsavedMenuDialogModes.PACK_HOTKEYS)
    setUnsavedMenuDialogElement(null)
    setUnsavedMenuDialogOpen(true)
  }

  return (
    <>
      <UnsavedChangesPrompt when={menuUpdated} title="Unsaved Changes" />
      <Grid container>
        <Grid item xs={12}>
          <Card>
            <CardHeader color="danger" icon>
              <Grid container>
                <Grid item xs={3} sm={3}>
                  <CardIcon color="danger">
                    <AppsIcon />
                  </CardIcon>
                </Grid>
                <Grid item xs={8} sm={6}>
                  <SiteSelector
                    siteList={siteList}
                    onSelect={onSiteSelected}
                    isGridItem={false}
                    autoSelect={false}
                    disabled={menuUpdated}
                    disabledTooltipText="Save changes to re-enable site selection" />
                </Grid>
                <Grid item xs={1} sm={3}></Grid>
              </Grid>
            </CardHeader>
            <CardBody>
              {
                isLookupDataLoading
                  ? <LinearProgress />
                  : selectedSiteId !== null &&
                <>
                  <Grid container>
                    <Grid item xl={2} lg={1} md={false} />
                    <Grid item xl={8} lg={10} md={12} sm={12} xs={12}>
                      <AtlasButtonBar>
                        <Box flexGrow={1} display="flex" alignItems="center">
                          <Button variant="contained" color="primary" onClick={handleSaveClick} disabled={!menuUpdated || !hotkeyElementEditorFormValid}>
                            {
                              isSaving === true
                                ? <><CircularProgress color="inherit" size={20} />&nbsp;&nbsp;</>
                                : <><SaveIcon />&nbsp;</>
                            }
                            {saveButtonText}
                          </Button>
                          &nbsp;
                          <ButtonGroup variant="text" color="primary">
                            {
                              currentMenuType === 'root' &&
                              <Button onClick={() => handleAddElementClick('menu')}><AllCategories stroke="green" strokeWidth={0.1}/>&nbsp;Add Menu</Button>
                            }
                            {
                              currentMenuType === 'menu' &&
                              <Button onClick={() => handleAddElementClick('submenu')}><NoChosenCategory stroke="#3653b0" strokeWidth={0.1}/>&nbsp;Add Submenu</Button>
                            }
                            {
                              // button to open a selected menu / submenu
                              (selectedElementIndex !== null && (menuData[selectedElementIndex].type === 'menu' || menuData[selectedElementIndex].type === 'submenu')) &&
                              <Button onClick={handleOpenSelectedMenu}><LaunchIcon />&nbsp;Open {menuData[selectedElementIndex].type}</Button>
                            }
                          </ButtonGroup>
                          &nbsp;
                          <ButtonGroup variant="text" color="primary">
                            <Button onClick={() => handleAddElementClick('product')}><AddCircleIcon />&nbsp;Add Product</Button>
                            <Button onClick={() => handleAddElementClick('subheading')}><TitleIcon />&nbsp;Add Subheading</Button>
                            <Button onClick={() => handleAddElementClick('spacer')}><HeightIcon />Add Spacer</Button>
                            {/* <Button onClick={() => handleAddElementClick('linebreak')}><WrapTextIcon />&nbsp;Add Linebreak</Button> */}
                          </ButtonGroup>
                        </Box>
                        <Button variant="contained" color="secondary" disabled={isPublishing} onClick={attemptToPublishHotkeys}><CloudUploadIcon />&nbsp;&nbsp;Publish All</Button>
                      </AtlasButtonBar>
                    </Grid>
                    <Grid item xl={2} lg={1} md={false} />

                    <Grid item xl={2} lg={1} md={false} />
                    <Grid item xl={5} lg={6} md={12} sm={12} xs={12} className={classes.leftPanel}>
                      <Box position="relative" display="flex" justifyContent="center" className={classes.panel}>
                        <Box>
                          <HotkeyNavigationBar
                            selectedMenu={currentMenu}
                            selectedSubMenu={currentSubMenu}
                            onHomeClick={handleNavigationHomeClick}
                            onMenuClick={handleNavigationMenuClick}
                          />

                          <Box className={classes.menuWrapper}>
                            {
                              isMenuLoading
                                ? <Box display="flex" justifyContent="center" alignItems="center" height="100%">Loading...</Box>
                                : <>
                                  <HotkeyMenu
                                    elementData={menuData}
                                    selectedElement={menuData[selectedElementIndex]}
                                    onElementClick={handleElementClick}
                                    onElementDoubleClick={handleElementDoubleClick}
                                  />
                                  {/* for some reason, I can't get the styles on the element below to work in the css useStyles */}
                                  <Box className={classes.menuFade} style={{ pointerEvents: 'none', backgroundImage: 'linear-gradient(to bottom, rgba(248,248,248,0), rgba(248,248,248,1) 100%' }}></Box>
                                </>
                            }
                          </Box>
                        </Box>
                        {
                          isSaving &&
                          <GlassPanel message="Saving..." />
                        }
                      </Box>
                    </Grid>
                    <Grid item xl={3} lg={4} md={12} sm={12} xs={12} className={classes.rightPanel}>
                      <Box display="flex" justifyContent="center" className={classes.panel}>
                        {
                          selectedElementIndex !== null
                            ? <HotkeyElementEditor
                              data={menuData[selectedElementIndex]}
                              icons={icons}
                              colors={colors}
                              fontSizes={fontSizes}
                              widths={widths}
                              onDataChanged={handleElementDataChanged}
                              onDeleteElementClick={handleDeleteElementClick}
                              onPriorityUpClick={handleElementPriorityUpClick}
                              onPriorityDownClick={handleElementPriorityDownClick}
                              onProductSelectClick={handleProductSelectClick}
                              onFormValidationStatusChange={handleHotkeyElementEditorFormValidationStatusChange}
                            />
                            : <Box height={50} display="flex" justifyContent="center" alignItems="center">Select an Element to edit, or Add a new one above</Box>
                        }
                        {
                          isSaving &&
                          <GlassPanel />
                        }
                      </Box>
                    </Grid>
                  </Grid>
                </>
              }
              <PublishingPanel publishing={isPublishing} />
            </CardBody>
          </Card>
        </Grid>
      </Grid>
      {
        selectedSiteId !== null && <SelectProductDialog siteId={selectedSiteId} title="Select Hotkey Product" open={productSelectDialogOpen} onProductSelect={handleProductSelect} onClose={handleProductSelectDialogClose} />
      }
      <UnsavedMenuDialog mode={unsavedMenuDialogMode} show={menuUpdated} open={unsavedMenuDialogOpen} onContinue={handleUnsavedMenuDialogContinue} onClose={handleUnsavedMenuDialogClose} menuElement={unsavedMenuDialogElement} />
      <DeleteElementDialog open={deleteElementDialogOpen} onDelete={handleDeleteElement} onClose={handleDeleteElementDialogClose} />
      <PublishDialog open={publishDialogOpen} onClose={handlePublishDialogClose} onPublish={handlePublish} />
      <Snackbar open={snackbarOpen} autoHideDuration={5000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleSnackbarClose}>
        <Alert severity={snackbarSeverity} variant="filled">{snackbarMessage}</Alert>
      </Snackbar>
    </>
  )
}
